import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { PlainButton } from 'folio-common-components';
import { isArrayOfAtLeastOne } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import type { ReceiptItemFragment } from '../../common-client-queries.generated';
import { InfoIcon, TrashcanIcon } from '../../icons';
import { ConfirmDialog } from '../confirm-dialog';
import {
  DeleteReceiptDocument,
  DeleteStandaloneReceiptDocument,
} from './queries.generated';

const ReceiptImage = styled.a<{ backgroundColor: string | null }>`
  display: block;
  border-radius: 8px;
  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : colors.background};
  padding-top: 100%;

  ::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: inherit;
  }

  :focus::after {
    border-color: ${colors.blue};
  }
`;

const ReceiptPreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  position: absolute;
  top: 0;
`;

const TrashcanIconButton = styled(PlainButton)`
  display: flex;
  position: absolute;
  right: 5px;
  bottom: 5px;
  border: 1px solid ${colors.grayMedium};
  border-radius: 4px;
  background-color: white;

  :focus {
    border-color: ${colors.blue};
  }
`;

const StyledTrashcanIcon = styled(TrashcanIcon)`
  color: ${colors.wcagRed};
  margin: 8px;
`;

type Receipt = Pick<
  ReceiptItemFragment,
  '__typename' | 'fid' | 'images' | 'isInvoice'
>;

interface DeleteButtonProps {
  imageNr: number;
  receipt: Receipt;
  onDelete: () => void;
}

interface DeleteButtonEventProps extends DeleteButtonProps {
  eventFid: string;
}

const DeleteReceiptButton: React.FC<{
  deleteReceipt: () => Promise<unknown>;
  imageNr: number;
}> = ({ deleteReceipt, imageNr }) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const deleteOverlayState = useOverlayTriggerState({});

  return (
    <>
      <TrashcanIconButton
        aria-label={`Slett kvittering nummer ${imageNr}`}
        title="Slett kvittering"
        disabled={isDeleting}
        onClick={deleteOverlayState.open}
      >
        <StyledTrashcanIcon />
      </TrashcanIconButton>
      <ConfirmDialog
        title="Er du sikker på at du vil slette kvitteringen?"
        confirmText="Slett"
        onConfirm={() => {
          setIsDeleting(true);
          deleteReceipt();
        }}
        overlayState={deleteOverlayState}
      >
        Er du sikker på at du vil slette&nbsp;kvitteringen?
      </ConfirmDialog>
    </>
  );
};

const DeleteEventReceipt: React.FC<DeleteButtonEventProps> = ({
  imageNr,
  receipt,
  eventFid,
  onDelete,
}) => {
  const [deleteReceipt] = useMutation(DeleteReceiptDocument, {
    variables: { receiptFid: receipt.fid, eventFid },
    update(cache) {
      cache.evict({ id: cache.identify(receipt) });
      cache.gc();

      onDelete();
    },
  });

  return (
    <DeleteReceiptButton deleteReceipt={deleteReceipt} imageNr={imageNr} />
  );
};

const DeleteStandaloneReceipt: React.FC<DeleteButtonProps> = ({
  imageNr,
  receipt,
  onDelete,
}) => {
  const [deleteReceipt] = useMutation(DeleteStandaloneReceiptDocument, {
    variables: { receiptFid: receipt.fid },
    update(cache) {
      cache.evict({ id: cache.identify(receipt) });
      cache.gc();

      onDelete();
    },
  });

  return (
    <DeleteReceiptButton deleteReceipt={deleteReceipt} imageNr={imageNr} />
  );
};

export const ReceiptRow: React.FC<{
  eventFid: string | undefined;
  imageNr: number;
  receipt: Receipt;
  onDelete: () => void;
  'data-testid'?: string;
  editable: boolean;
}> = ({ eventFid, imageNr, receipt, onDelete, editable, ...props }) => {
  const { images, isInvoice } = receipt;

  const originalUrl = images.originalUrl;
  const srcSetCandidates: { src: string; size: string }[] = [];
  if (images.thumbnail256Url) {
    srcSetCandidates.push({ src: images.thumbnail256Url, size: '256w' });
  }
  if (images.thumbnail512Url) {
    srcSetCandidates.push({ src: images.thumbnail512Url, size: '512w' });
  }
  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <ReceiptImage
        href={originalUrl || undefined}
        backgroundColor={images.color}
        target="_blank"
        {...props}
      >
        {isArrayOfAtLeastOne(srcSetCandidates) ? (
          <ReceiptPreviewImage
            // This has to be set before srcSet, otherwise we get a double request in Safari
            loading="lazy"
            srcSet={srcSetCandidates
              .map(({ src, size }) => `${src} ${size}`)
              .join(', ')}
            // This has to be set after srcSet, otherwise we get a double request in Safari
            src={srcSetCandidates[0].src}
            sizes="512px"
            alt={`Kvittering nummer ${imageNr}`}
            // loading=lazy leads to a double request in Safari
            css={css`
              color: transparent;
            `}
          />
        ) : (
          <InfoIcon height={50} width={50} />
        )}
      </ReceiptImage>
      {editable && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {isInvoice ? null : eventFid ? (
            <DeleteEventReceipt
              receipt={receipt}
              eventFid={eventFid}
              imageNr={imageNr}
              onDelete={onDelete}
            />
          ) : (
            <DeleteStandaloneReceipt
              receipt={receipt}
              imageNr={imageNr}
              onDelete={onDelete}
            />
          )}
        </>
      )}
    </div>
  );
};
